/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import Button from "@mui/material/Button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
	Backdrop,
	CircularProgress,
	FormControl,
	TextField,
	Box,
	Stepper,
	Step,
	StepLabel,
	StepContent,
} from "@mui/material";
import toast from "react-hot-toast";
import CountryPicker from "components/mis-survey/CountryPicker";
import { ppmvsSchema } from "validators/ppmv";
import { STORAGE_KEY, usePPMVFormContext } from "context/ppmv/PpmvFormContext";
import FileUpload from "components/FileUpload";
import { useSubmitPPMVSurvey, useUploadFile } from "api/hooks";
import FormBanner from "components/FormBanner";
import { useFormBannerContent } from "context/formBanner/FormBannerContext";

const PPMVSurvey = () => {
	const { ppmvFormData, dispatch: dispatchSurvey } = usePPMVFormContext();
	const { dispatch } = useFormBannerContent();

	const [activeStep, setActiveStep] = useState(0);

	const {
		register,
		handleSubmit,
		setValue,
		watch,
		formState: { errors, isValid },
	} = useForm({
		resolver: zodResolver(ppmvsSchema),
		defaultValues: ppmvFormData,
		mode: "all",
	});

	const formData = watch();

	const handleNext = () => {
		setActiveStep((prevActiveStep) => prevActiveStep + 1);
	};

	const handleRegNext = () => {
		updateStateRegNumber();
		setActiveStep((prevActiveStep) => prevActiveStep + 1);
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => Math.max(prevActiveStep - 1, 0));
	};

	const endSurvey = () => {
		dispatchSurvey({ type: "CLEAR" });
		localStorage.removeItem(STORAGE_KEY);

		setTimeout(() => {
			dispatch({
				type: "UPDATE",
				payload: {
					title: "",
					subTitle: "",
					description: ``,
					backgroundImageUrl:
						"bg-[url('/public/images/ppmv/welcome-bg.jpg')]",
					formCompletedAt: null,
					screen: "THANK_YOU",
				},
			});
		}, 1000);
	};

	const updateStateRegNumber = () => {
		dispatchSurvey({
			type: "UPDATE",
			payload: { ...ppmvFormData, reg_no: formData.reg_no },
		});
	};

	const updateOutletImage = (imageUrl) => {
		dispatchSurvey({
			type: "UPDATE",
			payload: { ...ppmvFormData, outlet_img_url: imageUrl },
		});
		setValue("outlet_img_url", imageUrl, {
			shouldDirty: true,
			shouldTouch: true,
			shouldValidate: true,
		});
		toast.success("file uploaded successfully");
	};

	const updateIntletImage = (imageUrl) => {
		dispatchSurvey({
			type: "UPDATE",
			payload: { ...ppmvFormData, inlet_img_url: imageUrl },
		});
		setValue("inlet_img_url", imageUrl, {
			shouldDirty: true,
			shouldTouch: true,
			shouldValidate: true,
		});
		toast.success("file uploaded successfully");
	};

	const updateCertificateImage = (imageUrl) => {
		dispatchSurvey({
			type: "UPDATE",
			payload: { ...ppmvFormData, cert_img_url: imageUrl },
		});
		setValue("cert_img_url", imageUrl, {
			shouldDirty: true,
			shouldTouch: true,
			shouldValidate: true,
		});
		toast.success("certificate uploaded successfully");
	};

	const { isLoading, isError, uploadFile } = useUploadFile({
		onSuccess: (data) => {
			if (activeStep === 1) {
				updateCertificateImage(data);
			}

			if (activeStep === 2) {
				updateIntletImage(data);
			}

			if (activeStep === 3) {
				updateOutletImage(data);
			}
		},
		onError: (error) => {
			toast.error(error || "Failed to upload file");
		},
	});

	const submitData = useSubmitPPMVSurvey({
		onSuccess: () => {
			setTimeout(() => {
				toast.success("survey submitted successfully");
				dispatch({
					type: "UPDATE",
					payload: {
						title: "",
						subTitle: "",
						description: ``,
						backgroundImageUrl:
							"bg-[url('/public/images/ppmv/welcome-bg.jpg')]",
						formCompletedAt: null,
						screen: "THANK_YOU",
					},
				});
			}, 1000);
		},
		onError: (error) => {
			console.log({ error });
			toast.error(
				error?.response?.data?.message || "Failed to submit survey"
			);

			if (
				error?.response?.data?.message ===
				"You have already participated in the survey."
			) {
				endSurvey();
			}
			// else {
			// 	navigate("/survey/retry", { replace: true });
			// }
		},
	});

	const onSubmit = (data) => {
		dispatchSurvey({
			type: "UPDATE",
			payload: { ...ppmvFormData, ...data },
		});
		const completeData = { ...ppmvFormData, ...data };
		console.log({ data, completeData });
		submitData.submitSurvey(completeData);
	};

    console.log({ errors, isValid, ppmvFormData });

	return (
		<section className="bg-primary">
			<FormBanner />
			<section className="absolute top-auto [@media(min-width:260px)]:top-[38dvh] w-full bg-white [@media(min-width:260px)]:rounded-t-[38px] min-h-[66dvh]">
				<div className="relative max-w-[90dvw] 2xl:max-w-screen-xl mx-auto py-10">
					<form
						onSubmit={handleSubmit(onSubmit)}
						className="flex flex-col gap-8 w-full"
					>
						<Backdrop
							sx={(theme) => ({
								color: "#fff",
								zIndex: theme.zIndex.drawer + 3,
							})}
							open={(!isError && isLoading) || isLoading}
						>
							<CircularProgress color="inherit" />
						</Backdrop>

						<FormControl fullWidth>
							<div className="w-full relative">
								<p className="mb-4">Whatsapp Number</p>
								<TextField
									{...register("whatsapp_number", {
										required: true,
									})}
									id="whatsapp_number"
									label="Enter Whatsapp Number"
									variant="outlined"
									size="small"
									type="number"
									placeholder="08076542341"
									className="py-8 w-full !pr-2"
									error={Boolean(errors.whatsapp_number)}
									onWheel={(e) => e.target.blur()}
								/>
								<div className="bg-[#E5E7EB] w-6 h-8 absolute right-5 top-[55%]"></div>
							</div>

							{errors.whatsapp_number && (
								<small className="text-danger font-light italic mt-2 block">
									{errors.whatsapp_number?.message || ""}
								</small>
							)}
						</FormControl>

						<FormControl fullWidth>
							<div className="w-full relative">
								<p className="mb-4">Phone number</p>
								<TextField
									{...register("phone_number", {
										required: true,
									})}
									id="phone_number"
									label="Enter phone number"
									variant="outlined"
									size="small"
									type="number"
									placeholder="08076542341"
									className="py-8 w-full"
									error={Boolean(errors.phone_number)}
									onWheel={(e) => e.target.blur()}
								/>
								<div className="bg-[#E5E7EB] w-6 h-8 absolute right-5 top-[55%]"></div>
							</div>

							{errors.phone_number && (
								<small className="text-danger font-light italic mt-2 block">
									{errors.phone_number?.message || ""}
								</small>
							)}
						</FormControl>

						<FormControl fullWidth>
							<div className="w-full">
								<p className="mb-4">Email Address</p>
								<TextField
									{...register("email", {
										required: true,
									})}
									id="email"
									type="email"
									label="Enter email address"
									variant="outlined"
									size="small"
									placeholder="owolabi@gmail.com"
									className="py-8 w-full"
									error={Boolean(errors.email)}
								/>
							</div>

							{errors.email && (
								<small className="text-danger font-light italic mt-2 block">
									{errors.email?.message || ""}
								</small>
							)}
						</FormControl>

						<FormControl fullWidth>
							<div className="w-full">
								<p className="mb-4">Full Name</p>
								<TextField
									{...register("contact_name", {
										required: true,
									})}
									id="contact_name"
									label="Enter your Name"
									variant="outlined"
									size="small"
									placeholder="John doe"
									className="py-8 w-full"
									error={Boolean(errors.contact_name)}
								/>
							</div>

							{errors.contact_name && (
								<small className="text-danger font-light italic mt-2 block">
									{errors.contact_name?.message || ""}
								</small>
							)}
						</FormControl>

						<FormControl fullWidth>
							<div className="w-full">
								<p className="mb-4">Educational Background</p>
								<TextField
									{...register("educational_background", {
										required: true,
									})}
									id="educational_background"
									label="Enter Education"
									variant="outlined"
									size="small"
									placeholder="Primary, Secondary, none, etc"
									className="py-8 w-full"
									error={Boolean(
										errors.educational_background
									)}
								/>
							</div>

							{errors.educational_background && (
								<small className="text-danger font-light italic mt-2 block">
									{errors.educational_background?.message ||
										""}
								</small>
							)}
						</FormControl>

						<FormControl fullWidth>
							<div className="w-full">
								<p className="mb-4">Qualification</p>
								<TextField
									{...register("qualification", {
										required: true,
									})}
									id="qualification"
									label="Enter Qualification"
									variant="outlined"
									size="small"
									placeholder="BSc in science, none, etc"
									className="py-8 w-full"
									error={Boolean(errors.qualification)}
								/>
							</div>

							{errors.qualification && (
								<small className="text-danger font-light italic mt-2 block">
									{errors.qualification?.message || ""}
								</small>
							)}
						</FormControl>

						<div className="w-full text-gray-200">
							<CountryPicker
								onLocationChange={(locationData) => {
									setValue(
										"lat",
										locationData.latitude
											? locationData.latitude.toString()
											: "",
										{
											shouldValidate: true,
											shouldTouch: true,
										}
									);
									setValue(
										"lng",
										locationData.longitude
											? locationData.longitude.toString()
											: "",
										{
											shouldValidate: true,
											shouldTouch: true,
											shouldDirty: true,
										}
									);
									setValue(
										"state",
										locationData.userLocation
											? locationData.userLocation
													?.state || ""
											: "",
										{
											shouldValidate: true,
											shouldTouch: true,
											shouldDirty: true,
										}
									);
									setValue(
										"lga",
										locationData.userLocation
											? locationData.userLocation?.lga ||
													""
											: "",
										{
											shouldValidate: true,
											shouldTouch: true,
											shouldDirty: true,
										}
									);
									setValue(
										"town",
										locationData.userLocation
											? locationData.userLocation?.town ||
													""
											: "",
										{
											shouldValidate: true,
											shouldTouch: true,
											shouldDirty: true,
										}
									);
								}}
								index={0}
							/>
							{(errors.lat ||
								errors.lng ||
								errors.state ||
								errors.lga ||
								errors.town) && (
								<small className="text-danger">
									{errors.lat?.message ||
										errors.lng?.message ||
										errors.state?.message ||
										errors.lga?.message ||
										errors.town?.message}
								</small>
							)}
						</div>

						<FormControl fullWidth>
							<div className="w-full">
								<p className="mb-4">Shop Name</p>
								<TextField
									{...register("shop_name", {
										required: true,
									})}
									id="shop_name"
									label="Enter your Shop name"
									variant="outlined"
									size="small"
									placeholder="Owolabi Pharmacy"
									className="py-8 w-full"
									error={Boolean(errors.shop_name)}
								/>
							</div>

							{errors.shop_name && (
								<small className="text-danger font-light italic mt-2 block">
									{errors.shop_name?.message || ""}
								</small>
							)}
						</FormControl>

						<FormControl fullWidth>
							<div className="w-full">
								<p className="mb-4">Shop Address</p>
								<TextField
									{...register("shop_address", {
										required: true,
									})}
									id="shop_address"
									label="Enter your Shop address"
									variant="outlined"
									size="small"
									placeholder="21 ade street, kaduna"
									className="py-8 w-full"
									error={Boolean(errors.shop_address)}
								/>
							</div>

							{errors.shop_address && (
								<small className="text-danger font-light italic mt-2 block">
									{errors.shop_address?.message || ""}
								</small>
							)}
						</FormControl>

						<FormControl fullWidth>
							<div className="w-full">
								<p className="mb-4">
									What type of outlet do you operate?
								</p>
								<TextField
									{...register(
										"what_type_of_outlet_do_you_operate",
										{
											required: true,
										}
									)}
									id="what_type_of_outlet_do_you_operate"
									label="Type outlet here"
									variant="outlined"
									size="small"
									placeholder="pharmacy, small-shop, kiosk etc"
									className="py-8 w-full"
									error={Boolean(
										errors.what_type_of_outlet_do_you_operate
									)}
								/>
							</div>

							{errors.what_type_of_outlet_do_you_operate && (
								<small className="text-danger font-light italic mt-2 block">
									{errors.what_type_of_outlet_do_you_operate
										?.message || ""}
								</small>
							)}
						</FormControl>

						{Boolean(formData.phone_number) && (
							<Stepper
								activeStep={activeStep}
								orientation="vertical"
							>
								<Step className="">
									<StepLabel
										optional={
											<p className="text-sm">
												First step
											</p>
										}
									>
										<p className="text-xl">PCN number</p>
									</StepLabel>
									<StepContent>
										<FormControl fullWidth>
											<div className="w-full">
												<p className="mb-4 mt-5">
													What is your PCN (Pharamacy
													Council of Nigeria )
													registration number?
												</p>
												<TextField
													{...register("reg_no", {
														required: true,
													})}
													id="reg_no"
													label="enter number here"
													variant="outlined"
													size="small"
													className="py-8 w-full block mb-4"
													error={Boolean(
														errors.reg_no
													)}
												/>
											</div>
											{errors.reg_no && (
												<small className="text-danger font-light italic mt-2 block">
													{errors.reg_no?.message ||
														""}
												</small>
											)}
										</FormControl>
										<Box sx={{ mb: 2, mt: 2 }}>
											<Button
												variant="contained"
												onClick={handleRegNext}
												sx={{ mt: 1, mr: 1 }}
												className="disabled-btn"
											>
												Continue
											</Button>
											{ppmvFormData?.reg_no !== "" && (
												<Button
													disabled={false}
													onClick={handleNext}
													sx={{ mt: 1, mr: 1 }}
													className="dark:!text-gray-400"
												>
													Next
												</Button>
											)}
										</Box>
									</StepContent>
								</Step>
								<Step>
									<StepLabel
										optional={
											<p className=" text-sm">
												Second step
											</p>
										}
									>
										<p className="text-xl ">
											Capture PCN Certificate
										</p>
										<small className="">
											Take a picture of your PCN
											certificate
										</small>
									</StepLabel>
									<StepContent>
										<FileUpload
											onConfirm={(imageFile) => {
												uploadFile({
													file: imageFile,
													folderPath:
														formData.reg_no.trim() ||
														formData.phone_number.trim(),
												});
											}}
											initialUrl={formData.cert_img_url}
										/>
										<Box sx={{ mb: 2 }}>
											<Button
												variant="contained"
												onClick={handleNext}
												sx={{ mt: 1, mr: 1 }}
												className="disabled-btn"
											>
												Continue
											</Button>
											<Button
												disabled={false}
												onClick={handleBack}
												sx={{ mt: 1, mr: 1 }}
												className="dark:!text-gray-400"
											>
												Back
											</Button>
										</Box>
									</StepContent>
								</Step>

								<Step>
									<StepLabel
										optional={
											<p className=" text-sm">
												Third step
											</p>
										}
									>
										<p className="text-xl ">
											Shop Interior Picture{" "}
										</p>
										<small className="">
											Take a picture of you inside the
											shop
										</small>
									</StepLabel>
									<StepContent>
										<FileUpload
											onConfirm={(imageFile) => {
												uploadFile({
													file: imageFile,
													folderPath:
														formData.reg_no.trim() ||
														formData.phone_number.trim(),
												});
											}}
											initialUrl={formData.inlet_img_url}
										/>
										<Box sx={{ mb: 2 }}>
											<Button
												variant="contained"
												onClick={handleNext}
												sx={{ mt: 1, mr: 1 }}
												disabled={
													formData.inlet_img_url ===
														"" ||
													!formData.inlet_img_url
												}
												className="disabled-btn"
											>
												Continue
											</Button>
											<Button
												disabled={false}
												onClick={handleBack}
												sx={{ mt: 1, mr: 1 }}
												className="dark:!text-gray-400"
											>
												Back
											</Button>
										</Box>
									</StepContent>
								</Step>

								<Step>
									<StepLabel
										optional={
											<p className=" text-sm">
												Last step
											</p>
										}
									>
										<p className="text-xl ">
											Shop Exterior Picture{" "}
										</p>
										<small className="">
											Take a picture of you outside the
											shop with the shop name showing
										</small>
									</StepLabel>
									<StepContent>
										<FileUpload
											onConfirm={(imageFile) => {
												uploadFile({
													file: imageFile,
													folderPath:
														formData.reg_no.trim() ||
														formData.phone_number.trim(),
												});
											}}
											initialUrl={formData.outlet_img_url}
										/>
										<Box sx={{ mb: 2 }}>
											<Button
												variant="contained"
												onClick={handleNext}
												sx={{ mt: 1, mr: 1 }}
												disabled={
													formData.outlet_img_url ===
														"" ||
													!formData.outlet_img_url
												}
												className="disabled-btn"
											>
												Complete
											</Button>
											<Button
												disabled={false}
												onClick={handleBack}
												sx={{ mt: 1, mr: 1 }}
												className="dark:!text-gray-400"
											>
												Back
											</Button>
										</Box>
									</StepContent>
								</Step>
							</Stepper>
						)}

						{isValid && (
							<Button
								type="button"
								sx={{ mt: 4, mr: 1 }}
								onClick={handleBack}
								className=""
							>
								previous
							</Button>
						)}

						<button
							type="submit"
							className="bg-[#6D0000] text-white py-3 px-4 rounded-md w-4/5 mx-auto cursor-pointer"
							disabled={!isValid || submitData.isLoading}
						>
							{submitData.isLoading ? "Submitting" : "Submit"}
						</button>
					</form>
				</div>
			</section>
		</section>
	);
};

export default PPMVSurvey;
